import { MDXRemoteProps } from "next-mdx-remote";
import Image from "./_components/Image";
import Link from "./_components/Link";
import { twMerge } from "tailwind-merge";
import InArticleAd from "@/components/common/ads/InArticleAd";
import DisplayAd from "@/components/common/ads/DisplayAd";

export const mdxComponents: MDXRemoteProps["components"] = {
  a: ({ href, children }: any) => {
    return <Link href={href}>{children}</Link>;
  },
  hr: () => <div className="my-4 rounded-lg border-t-2 border-secondary " />,
  /** Wide GFM tables scroll inside the column instead of breaking the page. */
  table: ({ children, ...props }: any) => (
    <div className="w-full overflow-x-auto">
      <table {...props}>{children}</table>
    </div>
  ),
  Image: ({ src, alt, width, height }: any) => (
    <Image src={src} alt={alt} width={width} height={height} />
  ),
  InArticleAd: () => <InArticleAd />,
  DisplayAd: () => <DisplayAd />,
};

const wrapperBase =
  "prose prose-slate w-full max-w-full prose-headings:scroll-mt-24 prose-headings:text-primary";

/**
 * Prose scales are picked by what the markdown IS, not by how big it should
 * look. The two roles come straight from the content contracts:
 *
 * - `document`: the page's main content, written with its own `##`/`###`
 *   structure and usually paired with a table of contents (articles, policy
 *   pages, glossary terms, platform comparisons, handbook chapters, the
 *   wiki-like platform profile).
 * - `blurb`: paragraph-only copy embedded in a section or a card. The
 *   generators for these explicitly forbid headings, so the scale matches the
 *   surrounding UI copy instead of standing apart from it (location about,
 *   owner about, review summaries, the platform short description, the
 *   compare summary).
 */
const proseScale = {
  /**
   * `prose-lg` would render a markdown `# Title` at 48px. The handbook
   * chapters carry their own h1, so it is pinned to the same scale as every
   * other page title instead of overshooting it.
   */
  document:
    "prose-base sm:prose-lg prose-h1:text-3xl lg:prose-h1:text-4xl prose-h1:tracking-tight",
  blurb: "prose-sm sm:prose-base",
  /**
   * Long-form reading next to a sticky rail (handbook chapters): one step
   * below `document`, so a 4,000-word chapter stays a page, not a scroll
   * marathon, with headings pulled in to match.
   */
  reading:
    "prose-sm sm:prose-base prose-headings:tracking-tight prose-h2:text-xl sm:prose-h2:text-2xl prose-h3:text-base sm:prose-h3:text-lg",
  /**
   * The scale every surface used before the split. Kept for the listing
   * description only, which must keep rendering exactly as it did.
   */
  legacy: "prose-sm sm:prose-lg",
} as const;

export type ProseVariant = keyof typeof proseScale;

export type WrapperVariants = {
  variant?: ProseVariant;
};

type Props = {
  children: React.ReactNode;
  className?: string;
  id?: string;
} & WrapperVariants;

export const MDXWrapper = ({
  children,
  className,
  variant = "blurb",
  id,
}: Props) => {
  return (
    <div
      id={id}
      className={twMerge(wrapperBase, proseScale[variant], className)}
    >
      {children}
    </div>
  );
};
